import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import AuthActionPage from "./components/auth-action/AuthActionPage";
import { blocksDarkClass, readColorMode, readHighContrast } from "./utils/colorMode";
import { createHashRouter, RouterProvider } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import LanguageContextPrivider from "./components/contexts/LanguageContext";
import { OEMProvider } from "./components/contexts/OEMContext";
import { startAppIconSync } from "./utils/appIconSeries";

const container = document.getElementById("root");

function isAuthActionPage() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode");
  const path = window.location.pathname.replace(/\/+$/, "");
  return path === "/auth/action" || ((mode === "resetPassword" || mode === "verifyEmail" || mode === "recoverEmail") && Boolean(params.get("oobCode")));
}

try {
  sessionStorage.removeItem("nuage-hard-refresh");
} catch {
  // Session storage can be unavailable in a private frame.
}

function isTextField(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

function inTauriWindow() {
  const internals = window as Window & { __TAURI_INTERNALS__?: unknown; __TAURI__?: unknown };
  return internals.__TAURI_INTERNALS__ !== undefined || internals.__TAURI__ !== undefined;
}

window.addEventListener("keydown", (event) => {
  if (!inTauriWindow()) return;
  const key = event.key;
  const text = isTextField(event.target);
  const ctrl = event.ctrlKey || event.metaKey;
  const dev = process.env.NODE_ENV !== "production";
  const reload = !dev && (key === "F5" || (ctrl && (key === "r" || key === "R" || key === "F5")));
  const devtools = !dev && (key === "F12" || (ctrl && event.shiftKey && (key === "i" || key === "I" || key === "j" || key === "J" || key === "c" || key === "C")));
  const functionKey = /^F([1-9]|1[0-2])$/.test(key) && !(dev && (key === "F5" || key === "F12"));
  const browserCommand = ctrl && !event.altKey && ["p", "P", "s", "S", "o", "O", "u", "U", "f", "F", "g", "G", "h", "H", "l", "L", "n", "N", "t", "T", "w", "W", "j", "J"].includes(key);
  const zoom = ctrl && (key === "+" || key === "-" || key === "=" || key === "0" || key === "_" || event.code === "NumpadAdd" || event.code === "NumpadSubtract");
  if (reload || devtools || functionKey || browserCommand || zoom || key === "ContextMenu") {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  if (!text && ctrl && (key === "a" || key === "A")) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  if (!text && !ctrl && key === "Alt") {
    event.preventDefault();
    return;
  }
  if (!text && ["Tab", " ", "Enter", "PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Escape"].includes(key)) {
    event.preventDefault();
  }
}, true);

window.addEventListener("contextmenu", (event) => {
  event.preventDefault();
}, true);

window.addEventListener("selectstart", (event) => {
  if (isTextField(event.target)) return;
  event.preventDefault();
}, true);

// Add warning before closing or navigating away
window.addEventListener('beforeunload', (event) => {
  if (process.env.NODE_ENV !== "production") return;
  if (isAuthActionPage()) return;
  try {
    if (sessionStorage.getItem("nuage-hard-refresh") === "1") return;
  } catch {
    // Keep the leave warning when session storage cannot be read.
  }
  const hashPath = window.location.hash.replace(/^#/, "");
  if (hashPath === "/customer" || hashPath.startsWith("/customer/") || hashPath === "/tray-exit") return;
  console.log("beforeunload");
  const message = "Are you sure you want to leave? Changes you made may not be saved.";
  event.preventDefault();
  event.returnValue = message;
  return message;
});

startAppIconSync();

if (container) {
  const hashPath = window.location.hash.replace(/^#/, "");
  if (readColorMode() === "dark" && !blocksDarkClass(hashPath)) {
    document.documentElement.classList.add("dark");
    if (readHighContrast()) document.documentElement.classList.add("contrast");
  }
  const root = createRoot(container);

  if (isAuthActionPage()) {
    root.render(
      <React.StrictMode>
        <AuthActionPage />
      </React.StrictMode>
    );
  } else {
  const router = createHashRouter([
    {
      path: "*",
      element: (
        <>
          <ScrollToTop />
          <OEMProvider>
            <LanguageContextPrivider>
              <App />
            </LanguageContextPrivider>
          </OEMProvider>
        </>
      ),
    },
  ]);

  root.render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  );
  }
}
